import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const REGIONS = ['North', 'South', 'East', 'West', 'Central', 'North-East'];

const REGION_COLORS = {
  North:        { bg: '#e8f5ee', text: '#086F3C', dot: '#086F3C' },
  South:        { bg: '#fdf0eb', text: '#F2652C', dot: '#F2652C' },
  East:         { bg: '#e8f0fe', text: '#1a73e8', dot: '#1a73e8' },
  West:         { bg: '#fef8e7', text: '#f9ab00', dot: '#f9ab00' },
  Central:      { bg: '#f4e9ff', text: '#9334e9', dot: '#9334e9' },
  'North-East': { bg: '#fce8f0', text: '#d93025', dot: '#d93025' },
};

const emptyForm = {
  name: '', code: '', city: '', state: '', region: 'North',
  address: '', managerName: '', phone: '', email: '', active: true,
  entraGroupId: '',
};

const Offices = () => {
  const [offices,       setOffices]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [regionFilter,  setRegionFilter]  = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editOffice,    setEditOffice]    = useState(null);
  const [form,          setForm]          = useState(emptyForm);
  const [saving,        setSaving]        = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState(null);   // single delete
  const [selected,      setSelected]      = useState([]);     // bulk selection (array of _id)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkDeleting,  setBulkDeleting]  = useState(false);

  const fetchOffices = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/offices/all');
      setOffices(data.offices);
      setSelected([]);
    } catch {
      toast.error('Failed to load offices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOffices(); }, [fetchOffices]);

  const openCreate = () => { setEditOffice(null); setForm(emptyForm); setShowModal(true); };
  const openEdit   = (o) => {
    setEditOffice(o);
    setForm({ name: o.name, code: o.code, city: o.city, state: o.state, region: o.region,
      address: o.address || '', managerName: o.managerName || '',
      phone: o.phone || '', email: o.email || '', active: o.active,
      entraGroupId: o.entraGroupId || '' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editOffice) {
        await api.patch(`/offices/${editOffice._id}`, form);
        toast.success('Office updated');
      } else {
        await api.post('/offices', form);
        toast.success('Office created');
      }
      setShowModal(false);
      fetchOffices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/offices/${deleteTarget._id}`);
      toast.success('Office deleted');
      setDeleteTarget(null);
      fetchOffices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
      setDeleteTarget(null);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/offices/bulk-delete', { ids: selected });
      toast.success(`${data.deleted} office${data.deleted !== 1 ? 's' : ''} deleted`);
      setShowBulkConfirm(false);
      fetchOffices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const filtered = offices.filter(o => {
    const q = search.toLowerCase();
    const matchQ = !q || o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
      || o.city.toLowerCase().includes(q) || o.state.toLowerCase().includes(q);
    const matchR = !regionFilter || o.region === regionFilter;
    return matchQ && matchR;
  });

  // Selection helpers
  const allChecked   = filtered.length > 0 && filtered.every(o => selected.includes(o._id));
  const someChecked  = filtered.some(o => selected.includes(o._id));

  const toggleAll = () => {
    if (allChecked) {
      setSelected(s => s.filter(id => !filtered.some(o => o._id === id)));
    } else {
      setSelected(s => [...new Set([...s, ...filtered.map(o => o._id)])]);
    }
  };

  const toggleOne = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const regionCounts = REGIONS.map(r => ({ region: r, count: offices.filter(o => o.region === r).length }));

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">

      {/* ── Header ── */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-0">
            <i className="bi bi-buildings-fill me-2 text-primary"></i>Office Management
          </h4>
          <p className="text-muted mb-0 small">
            {offices.length} offices across {REGIONS.filter(r => offices.some(o => o.region === r)).length} regions
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {selected.length > 0 && (
            <button
              className="btn btn-danger fw-semibold"
              onClick={() => setShowBulkConfirm(true)}
            >
              <i className="bi bi-trash3 me-1"></i>
              Delete selected ({selected.length})
            </button>
          )}
          <button className="btn btn-primary fw-semibold" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1"></i>Add Office
          </button>
        </div>
      </div>

      {/* ── Region filter chips ── */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          className={`btn btn-sm rounded-pill ${!regionFilter ? 'btn-dark' : 'btn-outline-secondary'}`}
          onClick={() => setRegionFilter('')}
        >
          All ({offices.length})
        </button>
        {regionCounts.filter(r => r.count > 0).map(({ region, count }) => {
          const c = REGION_COLORS[region];
          return (
            <button
              key={region}
              className="btn btn-sm rounded-pill fw-semibold"
              style={regionFilter === region
                ? { background: c.text, color: '#fff', border: `1px solid ${c.text}` }
                : { background: c.bg, color: c.text, border: `1px solid ${c.dot}44` }}
              onClick={() => setRegionFilter(regionFilter === region ? '' : region)}
            >
              <span className="me-1">●</span>{region} ({count})
            </button>
          );
        })}
      </div>

      {/* ── Search + selection status bar ── */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="input-group" style={{ maxWidth: 380 }}>
          <span className="input-group-text bg-white border-end-0">
            <i className="bi bi-search text-muted"></i>
          </span>
          <input
            type="text" className="form-control border-start-0 ps-0"
            placeholder="Search name, code, city…"
            value={search} onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="btn btn-outline-secondary border-start-0" onClick={() => setSearch('')}>
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
        {someChecked && (
          <span className="text-muted small">
            <strong>{selected.length}</strong> of {filtered.length} selected
            <button className="btn btn-link btn-sm p-0 ms-2 text-danger" onClick={() => setSelected([])}>
              Clear
            </button>
          </span>
        )}
      </div>

      {/* ── List table ── */}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-buildings fs-1 opacity-25 d-block mb-2"></i>
          No offices found
        </div>
      ) : (
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.88rem' }}>
              <thead style={{ background: '#f8f9fb' }}>
                <tr>
                  <th style={{ width: 44 }} className="ps-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="fw-semibold text-muted text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Office</th>
                  <th className="fw-semibold text-muted text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Code</th>
                  <th className="fw-semibold text-muted text-uppercase d-none d-md-table-cell" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Location</th>
                  <th className="fw-semibold text-muted text-uppercase d-none d-lg-table-cell" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Region</th>
                  <th className="fw-semibold text-muted text-uppercase d-none d-xl-table-cell" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Manager</th>
                  <th className="fw-semibold text-muted text-uppercase d-none d-xl-table-cell" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Phone</th>
                  <th className="fw-semibold text-muted text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>Status</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const c = REGION_COLORS[o.region] || REGION_COLORS.North;
                  const isSelected = selected.includes(o._id);
                  return (
                    <tr key={o._id}
                      style={{ background: isSelected ? '#f0f4ff' : undefined, cursor: 'default' }}
                    >
                      <td className="ps-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isSelected}
                          onChange={() => toggleOne(o._id)}
                        />
                      </td>
                      <td>
                        <div className="fw-semibold text-dark" style={{ lineHeight: 1.3 }}>{o.name}</div>
                        {o.address && (
                          <div className="text-muted d-md-none" style={{ fontSize: '0.75rem' }}>
                            {o.city}, {o.state}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-secondary border font-monospace" style={{ fontSize: '0.75rem' }}>
                          {o.code}
                        </span>
                      </td>
                      <td className="d-none d-md-table-cell text-muted">
                        <div>{o.city}</div>
                        <div style={{ fontSize: '0.75rem' }}>{o.state}</div>
                      </td>
                      <td className="d-none d-lg-table-cell">
                        <span className="badge rounded-pill px-2 py-1 fw-semibold"
                          style={{ background: c.bg, color: c.text, fontSize: '0.72rem' }}>
                          <span className="me-1">●</span>{o.region}
                        </span>
                      </td>
                      <td className="d-none d-xl-table-cell text-muted">{o.managerName || '—'}</td>
                      <td className="d-none d-xl-table-cell text-muted">{o.phone || '—'}</td>
                      <td>
                        {o.active
                          ? <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.72rem' }}>Active</span>
                          : <span className="badge bg-danger bg-opacity-10 text-danger"   style={{ fontSize: '0.72rem' }}>Inactive</span>}
                      </td>
                      <td>
                        <div className="d-flex gap-1 justify-content-end pe-2">
                          <button
                            className="btn btn-sm btn-outline-primary rounded-3 px-2"
                            title="Edit"
                            onClick={() => openEdit(o)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger rounded-3 px-2"
                            title="Delete"
                            onClick={() => setDeleteTarget(o)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer row */}
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top bg-white"
            style={{ fontSize: '0.8rem', color: '#888' }}>
            <span>Showing {filtered.length} of {offices.length} offices</span>
            {someChecked && (
              <span className="text-primary fw-semibold">{selected.length} selected</span>
            )}
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <form className="modal-content rounded-4 border-0 shadow-lg" onSubmit={handleSave}>
              <div className="modal-header border-0 pb-0 pt-4 px-4">
                <h5 className="modal-title fw-bold">{editOffice ? 'Edit Office' : 'Add New Office'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold">Office Name *</label>
                    <input className="form-control" required value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Office Code *</label>
                    <input className="form-control font-monospace text-uppercase" required maxLength={10}
                      value={form.code} placeholder="e.g. DEL-HQ"
                      onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">City *</label>
                    <input className="form-control" required value={form.city}
                      onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">State *</label>
                    <input className="form-control" required value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Region *</label>
                    <select className="form-select" required value={form.region}
                      onChange={e => setForm(f => ({ ...f, region: e.target.value }))}>
                      {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Address</label>
                    <input className="form-control" value={form.address}
                      onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Manager Name</label>
                    <input className="form-control" value={form.managerName}
                      onChange={e => setForm(f => ({ ...f, managerName: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Phone</label>
                    <input className="form-control" value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Email</label>
                    <input type="email" className="form-control" value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Entra ID Group (Object ID)
                      <span className="text-muted fw-normal ms-2">— employee search shows only this group's members</span>
                    </label>
                    <input className="form-control font-monospace" value={form.entraGroupId}
                      placeholder="e.g. 3f2a1b6c-9d4e-4f7a-b8c1-0e5d6a7b8c9d (leave blank = whole organization)"
                      onChange={e => setForm(f => ({ ...f, entraGroupId: e.target.value.trim() }))} />
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="officeActive"
                        checked={form.active}
                        onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                      <label className="form-check-label small fw-semibold" htmlFor="officeActive">
                        Office Active
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-light rounded-3"
                  onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary rounded-3 fw-semibold px-4" disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</>
                    : (editOffice ? 'Save Changes' : 'Create Office')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Single delete confirm ── */}
      {deleteTarget && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow-lg">
              <div className="modal-body p-4 text-center">
                <div className="text-danger mb-3"><i className="bi bi-exclamation-triangle-fill fs-1"></i></div>
                <h5 className="fw-bold">Delete Office?</h5>
                <p className="text-muted small mb-4">
                  Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <button className="btn btn-light rounded-3 px-4" onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className="btn btn-danger rounded-3 px-4 fw-semibold" onClick={handleDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk delete confirm ── */}
      {showBulkConfirm && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4 border-0 shadow-lg">
              <div className="modal-body p-4 text-center">
                <div className="text-danger mb-3"><i className="bi bi-trash3-fill fs-1"></i></div>
                <h5 className="fw-bold">Delete {selected.length} Offices?</h5>
                <p className="text-muted small mb-4">
                  This will permanently delete <strong>{selected.length} selected office{selected.length !== 1 ? 's' : ''}</strong>.
                  This action cannot be undone.
                </p>
                <div className="d-flex gap-2 justify-content-center">
                  <button className="btn btn-light rounded-3 px-4"
                    onClick={() => setShowBulkConfirm(false)} disabled={bulkDeleting}>Cancel</button>
                  <button className="btn btn-danger rounded-3 px-4 fw-semibold"
                    onClick={handleBulkDelete} disabled={bulkDeleting}>
                    {bulkDeleting
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting…</>
                      : `Delete ${selected.length} Offices`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offices;
